import AppStateService from '@ajs/services/AppStateService';
import { fdxUI as FdxUI } from '@ajs/services/fdxUI';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { RouteParam } from '@app/core/guards/enums/route-param.enum';
import { BigCommerceSelfServiceFeatureFlag } from '@app/core/models/enums/account-feature-flag.enum';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { ErrorService } from '@app/core/services/error.service';
import { LogService } from '@app/core/services/log.service';
import { DbModel } from '@app/databases/models/db.model';
import { DatabasesDataService } from '@app/databases/services/databases-data.service';
import { catchError, combineLatest, filter, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { LinkService } from '../services/link.service';

/**
 * This guard restricts Surface access based on their plan's provided configuration settings.
 * This currently supports permissions checks for "Free" and "Paid" account feature flags.
 *
 * If a user is an analyst, but is not viewing Surface mode (they are in FDX Classic mode), ignore the account feature flag and give them regular route guard access.
 *
 * If the user cannot access the route, it redirects the user the data page.
 */
@Injectable({
    providedIn: 'root'
})
export class SurfaceTierGuard implements CanActivate, OnDestroy {
    readonly unsubscribe$: Subject<void> = new Subject<void>();
    constructor(
        private readonly appStateService: AppStateService,
        private readonly bigCommerceMode: BigCommerceModeStateService,
        private readonly databaseApiService: DatabasesDataService,
        private readonly fdxUI: FdxUI,
        private readonly errorService: ErrorService,
        private readonly linkService: LinkService,
        private readonly logService: LogService,
        private readonly router: Router
    ) {}

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public canActivate(activatedRouteSnapshot: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        if (!activatedRouteSnapshot.data) {
            this.logService.error('[SurfaceTierGuard] missing data config for route.');
            return of(false);
        }

        const routePermissionsConfig: BigCommerceModeRoutePermissionsConfig = activatedRouteSnapshot.data['config'];
        if (!routePermissionsConfig?.[BigCommerceSelfServiceFeatureFlag.FREE] || !routePermissionsConfig?.[BigCommerceSelfServiceFeatureFlag.PAID]) {
            this.logService.error('[SurfaceTierGuard] missing data config for route.');
            return of(false);
        }

        // Wait until the user and account are loaded before we can determine what account level feature flag is set
        return combineLatest([this.appStateService.userLoaded$, this.appStateService.accountLoaded$])
            .pipe(
                filter(([userLoaded, accountLoaded]) => Boolean(userLoaded) && Boolean(accountLoaded)),
                take(1),
                map(() => {

                    // Calculate the restricted UI, and set the mode in the state service
                    const showRestrictedUI: boolean = this.bigCommerceMode.showRestrictedUI();

                    // If we are not restricting the UI for this user, allow them to access this route
                    // Ex: An analyst who is viewing an account with the BigCommerce Mode feature flag, but is not in demo mode.
                    // In this example, the account has a feature flag, but since they are NOT in
                    if (!showRestrictedUI) {
                        return true;
                    }

                    // Allow analysts to access certain routes even in BC SS mode (ex: accounts, or databases)
                    const user = this.appStateService.getUser();
                    if (user.isInDemoView() && this.appStateService.isPrivacyLevelAtLeastAnalyst(false) && routePermissionsConfig.hasAnalystAuthorization) {
                        return true;
                    }

                    let canAccess: boolean = false;

                    // Check the current BC SS UI mode permission configs to determine if the user can access the given route
                    switch (this.bigCommerceMode.getFeatureFlagMode()) {
                        case BigCommerceSelfServiceFeatureFlag.PAID:
                            canAccess = this.canAccessRoute(activatedRouteSnapshot, routePermissionsConfig[BigCommerceSelfServiceFeatureFlag.PAID]);
                            break;
                        case BigCommerceSelfServiceFeatureFlag.FREE:
                            canAccess = this.canAccessRoute(activatedRouteSnapshot, routePermissionsConfig[BigCommerceSelfServiceFeatureFlag.FREE]);
                            break;
                        case BigCommerceSelfServiceFeatureFlag.NONE:
                        default:
                            canAccess = true;
                            break;
                    }

                    return canAccess;
                }),
                switchMap((canAccess: boolean) => {
                    if (canAccess) {
                        return of(true);
                    }

                    // The user does not have access to this route
                    // If we have a database id saved in memory, use it and redirect
                    if (this.appStateService.getDatabaseId() !== null && this.appStateService.getDatabaseId() !== undefined) {
                        return of(this.router.createUrlTree(this.linkService.dataLink))
                    }

                    // We do not have the database id saved in memory - attempt to grab the db id from the URL
                    const dbid: string = activatedRouteSnapshot.paramMap.get(RouteParam.DatabaseId);
                    if (dbid !== undefined && dbid !== null) {
                        const databaseId: number = parseInt(dbid);

                        // if the database id is a number, use it to redirect to the data page
                        if (!isNaN(databaseId)) {
                            return of(this.router.createUrlTree(['/', databaseId, 'data', 0]));
                        }
                    }

                    // The route does not contain a database id in the url - make a call to grab the first active database for the account
                    return this.databaseApiService.getDatabases(this.appStateService.getAccountId()).pipe(
                        map((databases: DbModel[]) => {
                            const sortedDatabases: DbModel[] = databases.sort((a, b) => Number(a.id) - Number(b.id));

                            if (sortedDatabases.length) {
                                const dbWithLowestId: DbModel = sortedDatabases[0];
                                return this.router.createUrlTree(['/', dbWithLowestId.id, 'data', 0]);
                            }

                            // if there are no active databases, then do not allow access and keep the user where they are
                            this.fdxUI.showToastError('Unauthorized to access current URL.')
                            return false;
                        }),
                        catchError((error) => {
                            this.errorService.handleError(error)
                            return of(false);
                        }),
                        takeUntil(this.unsubscribe$)
                    )
                })
            );
    }

    /**
     * Compare the current routes query params against a list of unauthorized query params
     * @param route
     * @param unauthedQueryParams
     */
    containsUnauthedQueryParams(route: ActivatedRouteSnapshot, unauthedQueryParams?: UnAuthedQueryParam[]): boolean {
        if (!unauthedQueryParams?.length) {
            return false;
        }

        return unauthedQueryParams.some((unauthedQueryParam) => {
            const queryParamValue = route.queryParamMap.get(unauthedQueryParam.key);
            if (!queryParamValue) {
                return false;
            }

            // When the unauthorized query param exists - but there is no configuration value,
            // This indicates that the existence of the query param is not allowed.
            if (!unauthedQueryParam.value) {
                return true;
            }

            // Check to see if the specific unauthorized query param value is in the route
            return unauthedQueryParam.value.some((val) => {
                return queryParamValue.toLowerCase() === val.toLowerCase()
            });
        })
    }

    /**
     * Checks to see if the current route and query params are authorized.
     * If the BC SS user cannot access the route, it redirect the user to the given route.
     * @param activatedRouteSnapshot
     * @param bigCommerceModeRoutePermission
     */
    canAccessRoute(activatedRouteSnapshot: ActivatedRouteSnapshot, bigCommerceModeRoutePermission: BigCommerceModeRoutePermission): boolean {
        return bigCommerceModeRoutePermission.isRouteAuthorized && !this.containsUnauthedQueryParams(activatedRouteSnapshot, bigCommerceModeRoutePermission.unauthedQueryParams);
    }
}

interface UnAuthedQueryParam {
    key: string;
    value?: string[];
}

interface BigCommerceModeRoutePermission {
    /**
     * If the BC SS user is authorized access this route in their given mode
     */
    isRouteAuthorized: boolean;
    /**
     * A list of query params that BC SS users in this mode are unable to access
     * The key is the query param name, and the value is the associated value.
     *
     * Missing value - the key will always be unauthorized
     * Provided value - the specific key value pair will be unauthorized
     */
    unauthedQueryParams?: UnAuthedQueryParam[];
}

export interface BigCommerceModeRoutePermissionsConfig {
    /**
     */
    hasAnalystAuthorization?: boolean;
    [BigCommerceSelfServiceFeatureFlag.FREE]: BigCommerceModeRoutePermission;
    [BigCommerceSelfServiceFeatureFlag.PAID]: BigCommerceModeRoutePermission;
}
